<script
  generic="T extends DataTableProps['value'], TRow = T extends readonly (infer I)[] ? I : never"
  lang="ts"
  setup
>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type {
  DataTableProps,
  DataTableRowClickEvent,
  DataTableRowReorderEvent,
} from 'primevue/datatable';
import DataTable from 'primevue/datatable';
import { computed, ref, useTemplateRef, watch, onMounted } from 'vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import VButton from '../VButton/VButton.vue';
import VCallToAction from '../VCallToAction/VCallToAction.vue';
import VSkeleton from '../VSkeleton/VSkeleton.vue';
import type { Filters, Property, ValueColors } from './types';
// Registreert de custom matchmodes (o.a. containsAny), zodat ze ook werken voor
// filters die uit sessionStorage worden hersteld.
import './filterMatchModes';
import VColumn from './VColumn.vue';
import VDataTableFilters from './VDataTableFilters.vue';
import VDataTableSearch from './VDataTableSearch.vue';

const props = withDefaults(
  defineProps<{
    alwaysShowPaginator?: DataTableProps['alwaysShowPaginator'];
    autofocusSearch?: boolean;
    borderless?: boolean;
    currentPageReportTemplate?: DataTableProps['currentPageReportTemplate'];
    defaultEmptyDescription?: string;
    defaultEmptyTitle?: string;
    dataKey?: DataTableProps['dataKey'];
    exportable?: boolean;
    filterableProperties?: Array<Property<keyof TRow>>;
    globalFilterFields?: DataTableProps['globalFilterFields'];
    headerless?: boolean;
    paginator?: DataTableProps['paginator'];
    paginatorTemplate?: DataTableProps['paginatorTemplate'];
    loading?: DataTableProps['loading'];
    onRowReordered?: (event: DataTableRowReorderEvent) => void;
    removableSort?: DataTableProps['removableSort'];
    rows?: DataTableProps['rows'];
    scrollable?: DataTableProps['scrollable'];
    scrollHeight?: DataTableProps['scrollHeight'];
    searchable?: boolean;
    showRowArrow?: boolean;
    showAddButton?: boolean;
    addButtonLabel?: string;
    showFilterCounts?: boolean;
    sortField?: DataTableProps['sortField'];
    sortOrder?: DataTableProps['sortOrder'];
    reorderableColumns?: DataTableProps['reorderableColumns'];
    stateKey?: DataTableProps['stateKey'];
    stateStorage?: DataTableProps['stateStorage'];
    valueColors?: ValueColors<TRow>;
    excludeFilterPropertiesFromState?: Array<keyof TRow>;
  }>(),
  {
    addButtonLabel: 'Toevoegen',
    autofocusSearch: true,
    borderless: false,
    currentPageReportTemplate: 'van {totalPages}',
    defaultEmptyTitle: 'Deze lijst is leeg',
    exportable: false,
    paginator: true,
    paginatorTemplate:
      'FirstPageLink PrevPageLink JumpToPageInput CurrentPageReport NextPageLink LastPageLink',
    removableSort: true,
    rows: 50,
    scrollable: true,
    scrollHeight: 'flex',
    showRowArrow: undefined,
  },
);

const emit = defineEmits<{
  addButtonClicked: [value?: Event];
  rowClick: [value: DataTableRowClickEvent];
  page: [{ page: number; pageCount: number }];
}>();

defineExpose({ resetPagination });

const filters = defineModel<Filters<TRow>>('filters', { default: {} });
const model = defineModel<T>('value', { default: [], required: true });

const expandedRows = defineModel<DataTableProps['expandedRows']>('expandedRows', { default: [] });

const first = ref(0);
// Zie https://vuejs-language-tools.vercel.app/features/slots#how-to-handle-indeterminate-slot-types
const slots = defineSlots();

const heeftRijen = computed(() => (model.value?.length ?? 0) > 0);

const hasOnlySearchFilter = computed(
  () => Object.keys(filters.value).length === 1 && filters.value?.global,
);

const emptyFiltersMessage = computed(() => {
  if (hasOnlySearchFilter.value) {
    return `Niks kunnen vinden voor “${filters.value.global.value}”`;
  }

  if (Object.keys(filters.value).length) {
    return 'Niks kunnen vinden met de gekozen filters';
  }

  return props.defaultEmptyTitle;
});

const resetFilterButtonLabel = computed(() =>
  hasOnlySearchFilter.value ? 'Zoekfilter resetten' : 'Verwijder filters',
);

const dataTableRef = useTemplateRef('dataTableRef');

function exportCsv() {
  dataTableRef.value?.exportCSV();
}

function handleRowReorder(event: DataTableRowReorderEvent) {
  if (!props.onRowReordered) {
    model.value = event.value as unknown as T;
    return;
  }

  props.onRowReordered(event);
}

function resetFilters() {
  filters.value = {} as Filters<TRow>;
}

function resetPagination() {
  first.value = 0;
}

onMounted(() => {
  if (props.stateKey) {
    try {
      const raw = sessionStorage.getItem(props.stateKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        filters.value = parsed.filters ?? filters.value;
      }
    } catch {
      // ignore
    }
  }
});

watch(filters, resetPagination);
</script>

<template>
  <DataTable
    v-model:first="first"
    ref="dataTableRef"
    v-model:expanded-rows="expandedRows"
    row-group-mode="subheader"
    v-if="slots.default"
    v-bind="props"
    :loading="props.loading && heeftRijen"
    @row-click="(e: DataTableRowClickEvent) => emit('rowClick', e)"
    @row-reorder="handleRowReorder"
    @page="(e) => emit('page', e)"
    v-model:filters="filters"
  >
    <template
      v-if="
        props.filterableProperties || props.searchable || props.exportable || slots['header-start']
      "
      #header
    >
      <div class="flex gap-4 max-md:flex-col-reverse md:items-end">
        <!-- Ruimte links in de kopregel, bijvoorbeeld voor een teller of samenvatting van de lijst. -->
        <slot name="header-start"></slot>

        <VDataTableFilters
          v-if="props.filterableProperties"
          v-model="filters"
          :properties="props.filterableProperties"
          :values="model"
          :value-colors="props.valueColors"
          :show-counts="props.showFilterCounts"
        />

        <div class="flex flex-row gap-4 md:ml-auto">
          <slot name="search">
            <VDataTableSearch
              :autofocus="autofocusSearch"
              v-if="props.searchable"
              v-model="filters"
              class="shrink-0 md:w-[325px]"
            />
          </slot>
          <VTooltip label="Exporteren naar CSV" v-if="props.exportable">
            <VButton
              aria-label="Exporteren naar CSV"
              prepend-icon="far fa-file-csv"
              inverted
              @click="exportCsv()"
            />
          </VTooltip>
        </div>
      </div>
    </template>

    <slot></slot>
    <!-- Hotfix until https://github.com/primefaces/primevue/issues/5360 is solved -->
    <!-- eslint-disable-next-line vue/no-constant-condition -->
    <VColumn v-if="true" class="hidden" header="" field="" />

    <template v-if="props.showRowArrow">
      <!-- eslint-disable-next-line vue/no-constant-condition -->
      <VColumn v-if="true" field="row-arrow" class="w-0 pr-6">
        <template #body>
          <FontAwesomeIcon class="text-sm text-grey-400" :icon="['far', 'chevron-right']" />
        </template>
      </VColumn>
    </template>

    <template #empty>
      <div v-if="props.loading" class="px-6 py-4">
        <VSkeleton :count="5" height="lg" />
      </div>
      <VCallToAction
        v-else
        class="px-4 py-14"
        :description="props.defaultEmptyDescription"
        :icon="['fal', 'wind']"
        :title="emptyFiltersMessage"
      >
        <div class="flex flex-col gap-4">
          <VButton
            v-if="Object.keys(filters).length"
            inverted
            type="primary"
            @click="resetFilters"
            data-testid="reset-filter"
          >
            {{ resetFilterButtonLabel }}
          </VButton>

          <VButton
            v-if="showAddButton"
            align="center"
            inverted
            prepend-icon="fa-solid fa-plus"
            @click="(e) => emit('addButtonClicked', e)"
          >
            {{ props.addButtonLabel }}
          </VButton>
        </div>
      </VCallToAction>
    </template>

    <template #paginatorfirstpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevrons-left']" />
    </template>

    <template #paginatorprevpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevron-left']" />
    </template>

    <template #paginatornextpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevron-right']" />
    </template>

    <template #paginatorlastpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevrons-right']" />
    </template>

    <template #expansion="slotProps">
      <slot name="expansion" v-bind="slotProps"></slot>
    </template>

    <template v-if="props.showAddButton" #groupfooter>
      <VButton
        class="w-full"
        align="center"
        icon-only-responsiveness
        inverted
        prepend-icon="fa-solid fa-plus"
        @click="(e) => emit('addButtonClicked', e)"
      >
        {{ props.addButtonLabel }}
      </VButton>
    </template>

    <template v-else-if="slots.groupfooter" #groupfooter="slotProps">
      <slot name="groupfooter" :props="slotProps"></slot>
    </template>
  </DataTable>
</template>
